import React from 'react'
import { SparklesIcon } from '@heroicons/react/24/solid'

type Metric = {
  label: string
  value: React.ReactNode
  helper?: string
  trend?: string
  trendColor?: 'positive' | 'negative' | 'neutral'
}

const gradients: Record<'indigo' | 'emerald' | 'rose' | 'amber' | 'cyan', string> = {
  indigo: 'from-indigo-600 via-purple-600 to-blue-600',
  emerald: 'from-emerald-500 via-teal-500 to-cyan-500',
  rose: 'from-rose-500 via-fuchsia-500 to-purple-500',
  amber: 'from-amber-500 via-orange-500 to-red-500',
  cyan: 'from-cyan-500 via-blue-500 to-indigo-500'
}

interface StudentPageLayoutProps {
  title: string
  description?: string
  badge?: string
  gradient?: keyof typeof gradients
  actions?: React.ReactNode
  heroExtra?: React.ReactNode
  metrics?: Metric[]
  contentWidth?: 'default' | 'wide' | 'narrow'
  children: React.ReactNode
}

const trendClass = (trendColor: Metric['trendColor']) => {
  switch (trendColor) {
    case 'positive':
      return 'text-emerald-700 bg-emerald-50 border-emerald-200'
    case 'negative':
      return 'text-rose-700 bg-rose-50 border-rose-200'
    default:
      return 'text-slate-600 bg-slate-50 border-slate-200'
  }
}

export const StudentPageLayout: React.FC<StudentPageLayoutProps> = ({
  title,
  description,
  badge = 'STUDENT AREA',
  gradient = 'indigo',
  actions,
  heroExtra,
  metrics = [],
  contentWidth = 'default',
  children
}) => {
  const containerClass = {
    narrow: 'max-w-5xl',
    default: 'max-w-7xl',
    wide: 'max-w-[1600px]'
  }[contentWidth]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-8 sm:pb-16">
      {/* Hero 区域 */}
      <div className={`relative overflow-hidden bg-gradient-to-r ${gradients[gradient]} text-white`}>
        {/* 背景装饰 - 移动端隐藏部分装饰以提高性能 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-48 sm:w-96 h-48 sm:h-96 bg-white/20 rounded-full blur-3xl" />
          <div className="hidden sm:block absolute top-10 right-0 w-[500px] h-[500px] bg-cyan-300/20 rounded-full blur-3xl" />
          <div className="hidden sm:block absolute -bottom-20 left-1/3 w-72 h-72 bg-purple-300/20 rounded-full blur-3xl" />
          {/* 网格图案 */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
        </div>

        <div className={`relative ${containerClass} mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16`}>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-center">
            <div>
              {/* 徽章 */}
              <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em] uppercase border border-white/20">
                <SparklesIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                {badge}
              </span>

              {/* 标题 */}
              <h1 className="mt-3 sm:mt-5 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
                {title}
              </h1>

              {/* 描述 */}
              {description && (
                <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-white/80 leading-relaxed max-w-xl">
                  {description}
                </p>
              )}

              {/* 操作按钮 */}
              {actions && (
                <div className="mt-5 sm:mt-8 flex flex-col xs:flex-row flex-wrap gap-2 sm:gap-3">
                  {actions}
                </div>
              )}
            </div>

            {/* 额外内容 - 移动端优化 */}
            {heroExtra && (
              <div className="bg-white/10 rounded-2xl sm:rounded-3xl border border-white/20 backdrop-blur-xl p-4 sm:p-6 shadow-2xl mt-4 lg:mt-0">
                {heroExtra}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 指标卡片 */}
      {metrics.length > 0 && (
        <div className={`-mt-6 sm:-mt-10 ${containerClass} mx-auto px-3 sm:px-6 lg:px-8 relative z-10`}>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            {metrics.map((metric, index) => (
              <div
                key={metric.label}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-3 sm:p-5 hover:shadow-xl hover:border-slate-200 transition-all duration-300 group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* 顶部装饰 */}
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.2em] text-slate-400 font-semibold truncate">
                    {metric.label}
                  </p>
                  <div className="hidden sm:block w-2 h-2 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* 数值 */}
                <p className="text-lg sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2">{metric.value}</p>

                {/* 趋势/帮助文本 */}
                {(metric.helper || metric.trend) && (
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-[10px] sm:text-xs">
                    {metric.trend && (
                      <span
                        className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md border ${trendClass(
                          metric.trendColor ?? 'neutral'
                        )} font-medium`}
                      >
                        {metric.trend}
                      </span>
                    )}
                    {metric.helper && <span className="text-slate-500 truncate">{metric.helper}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <div className={`${containerClass} mx-auto px-3 sm:px-6 lg:px-8 mt-6 sm:mt-10 space-y-4 sm:space-y-8`}>
        {children}
      </div>
    </div>
  )
}

export default StudentPageLayout
